/**
 * Phase 66 Plan 03 — useProvisioningQueue (Plan 06 extends with optional `extra`).
 *
 * Backs the dashboard's "Provisioning queue" card (D-07). Fetches
 * `/api/v1/provisioning/queue` and computes the pending count + a
 * 7-day sparkline.
 *
 * Cache-key contract: `qk.provisioning(circuitId ?? "all")`.
 *
 * Plan 06 Task 2 (C-7): optional `extra` arg merged into the list query
 * options. CommandPalette passes `{ placeholderData: keepPreviousData }`
 * to keep the previous tenant's queue visible during a refetch window.
 *
 * @see js/pages/dashboard.js loadAll() lines 98-106 (legacy)
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { listProvisioningQueue } from "@/api/provisioning";
import { getSparkline } from "@/api/metrics";
import { useCircuitId } from "@/hooks/useCircuitId";
import { qk } from "@/lib/queryKeys";
import type { ProvisioningEntry } from "@/types/api";

type ExtraOpts<TData> = Partial<
  Omit<UseQueryOptions<TData, Error, TData>, "queryKey" | "queryFn">
>;

export function useProvisioningQueue(extra?: ExtraOpts<ProvisioningEntry[]>) {
  const circuitId = useCircuitId();
  const tenant = circuitId ?? "all";

  const list = useQuery({
    queryKey: qk.provisioning(tenant),
    queryFn: () => listProvisioningQueue(),
    refetchInterval: 30_000,
    staleTime: 10_000,
    ...extra,
  });

  const sparkPending = useQuery({
    queryKey: ["spark", "provisioning_pending", tenant] as const,
    queryFn: () => getSparkline("provisioning_pending"),
    staleTime: 5 * 60_000,
  });

  const queue = list.data ?? [];
  const pendingCount = queue.filter((q) => q.status === "pending").length;

  return {
    ...list,
    pendingCount,
    spark: sparkPending.data ?? [],
  };
}
